<template>
  <div
    class="apple-segmented-control"
    :class="[{ 'is-disabled': disabled }, 'appearance-' + appearance]"
    :role="isTabs ? 'tablist' : 'group'"
    :aria-disabled="disabled ? 'true' : undefined"
    :aria-orientation="isTabs ? 'horizontal' : undefined"
    v-bind="$attrs"
    @keydown="onKeydown"
  >
    <button
      v-for="(option, index) in options"
      :id="option.id || undefined"
      :key="option.value"
      ref="buttons"
      type="button"
      class="apple-segmented-option"
      :class="{ 'is-selected': option.value === value }"
      :disabled="disabled || option.disabled"
      :role="isTabs ? 'tab' : undefined"
      :aria-selected="isTabs ? String(option.value === value) : undefined"
      :aria-pressed="!isTabs ? String(option.value === value) : undefined"
      :aria-controls="isTabs ? option.controls : undefined"
      :tabindex="isTabs ? (index === tabStop ? 0 : -1) : undefined"
      @click="select(option)"
    ><slot name="option" :option="option">{{ option.label }}</slot></button>
  </div>
</template>

<script>
// Presentation-only: callers own the selected value and business actions.
export default {
  name: 'AppleSegmentedControl',
  inheritAttrs: false,
  props: {
    value: { type: [String, Number], default: '' },
    options: { type: Array, default: () => [] },
    mode: { type: String, default: 'pressed', validator: value => ['pressed', 'tabs'].includes(value) },
    appearance: { type: String, default: 'pills', validator: value => ['pills', 'segmented'].includes(value) },
    disabled: Boolean,
  },
  computed: {
    isTabs() { return this.mode === 'tabs'; },
    tabStop() {
      if (this.disabled) return -1;
      const selected = this.options.findIndex(option => !option.disabled && option.value === this.value);
      return selected >= 0 ? selected : this.options.findIndex(option => !option.disabled);
    },
  },
  methods: {
    select(option) {
      if (this.disabled || option.disabled || option.value === this.value) return;
      this.$emit('input', option.value);
      this.$emit('change', option.value);
    },
    onKeydown(event) {
      if (!this.isTabs || this.disabled || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      const buttons = this.$refs.buttons || [];
      const current = buttons.indexOf(event.target);
      if (current < 0) return;
      const enabled = this.options.map((option, index) => option.disabled ? -1 : index).filter(index => index >= 0);
      if (!enabled.length) return;
      event.preventDefault();
      let next;
      if (event.key === 'Home') next = enabled[0];
      else if (event.key === 'End') next = enabled[enabled.length - 1];
      else {
        const position = enabled.indexOf(current);
        const direction = event.key === 'ArrowRight' ? 1 : -1;
        next = enabled[(position + direction + enabled.length) % enabled.length];
      }
      this.select(this.options[next]);
      this.$nextTick(() => {
        if (!this._isBeingDestroyed && !this._isDestroyed && this.$refs.buttons?.[next]) this.$refs.buttons[next].focus({ preventScroll: true });
      });
    },
  },
};
</script>

<style scoped>
.apple-segmented-control { display: inline-flex; align-items: center; flex-wrap: wrap; gap: var(--apple-ui-space-1, 4px); min-width: 0; }
.apple-segmented-option { box-sizing: border-box; min-height: var(--apple-ui-control-height, 32px); padding: 4px 12px; border: 0; border-radius: var(--apple-ui-radius-control, 7px); background: color-mix(in srgb, var(--apple-ui-text, var(--color-text)) 5%, transparent); color: var(--apple-ui-text, var(--color-text)); font: inherit; font-size: 13px; line-height: 1.3; cursor: pointer; white-space: nowrap; }
.apple-segmented-option.is-selected { background: color-mix(in srgb, var(--apple-ui-accent, var(--color-primary)) 18%, transparent); color: var(--apple-ui-accent, var(--color-primary)); font-weight: 600; }
.apple-segmented-option:hover:not(:disabled) { filter: brightness(.96); }
.apple-segmented-option:active:not(:disabled) { filter: brightness(.90); }
.apple-segmented-option:disabled { opacity: .45; cursor: default; }
.apple-segmented-option:focus:not(:focus-visible) { outline: none; }
.apple-segmented-control .apple-segmented-option:focus-visible { outline: none; box-shadow: inset 0 0 0 2px color-mix(in srgb, var(--apple-ui-accent, var(--color-primary)) 82%, transparent); }
.apple-segmented-control.appearance-segmented { gap: 0; padding: 2px; border-radius: 8px; background: color-mix(in srgb, var(--apple-ui-text, var(--color-text)) 9%, transparent); }
.appearance-segmented .apple-segmented-option { min-height: 28px; padding-inline: 12px; border-radius: 6px; background: transparent; color: var(--apple-ui-muted, var(--color-secondary)); }
.appearance-segmented .apple-segmented-option.is-selected { background: var(--apple-ui-surface-raised, var(--color-secondary-bg)); color: var(--apple-ui-text, var(--color-text)); box-shadow: 0 1px 3px rgb(0 0 0 / 14%); }
.appearance-segmented .apple-segmented-option.is-selected:focus-visible { box-shadow: inset 0 0 0 2px var(--apple-ui-focus, var(--apple-ui-accent)), 0 1px 3px rgb(0 0 0 / 14%); }
@media (forced-colors: active) {
  .apple-segmented-option { border: 1px solid ButtonText; }
  .apple-segmented-option.is-selected { border-color: Highlight; color: Highlight; }
  .apple-segmented-control .apple-segmented-option:focus-visible { outline: 2px solid Highlight; outline-offset: -2px; }
}
</style>
